import { Menu, MenuItem, type MenuProps, alpha, menuItemClasses } from '@mui/material';
import { PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';

import { Text, api, getQueryProps, isDefined } from 'common';

import { AgenticChatSessionUpdatedTime } from './Sessions/UpdatedTime';
import { getAgenticChatSessionTitle } from './Sessions/title';
import { useAgenticChatContext } from './context';

interface AgenticChatRecentChatsMenuProps {
  id: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  anchorOrigin: MenuProps['anchorOrigin'];
  transformOrigin: MenuProps['transformOrigin'];
}

export function AgenticChatRecentChatsMenu(props: AgenticChatRecentChatsMenuProps) {
  const { id, anchorEl, onClose, anchorOrigin, transformOrigin } = props;
  const { sessionId, selectSession, createSessionMutation, startNewChat } = useAgenticChatContext();
  const { data, isPending, isError, refetch } = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findAll', {
      filterMode: 'OnlySessionsWithMessages',
    }),
    enabled: isDefined(anchorEl),
  });

  return (
    <Menu
      anchorEl={anchorEl}
      open={isDefined(anchorEl)}
      onClose={() => onClose()}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      variant="menu"
      slotProps={{
        paper: {
          sx: {
            width: 400,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'min(560px, calc(100dvh - 32px))',
          },
        },
        list: { id, 'aria-label': 'Recent chats', disablePadding: true },
      }}
    >
      <MenuItem
        divider
        disabled={createSessionMutation.isPending}
        onClick={() => {
          startNewChat();
          onClose();
        }}
        sx={{
          position: 'sticky',
          top: 0,
          // Keep New chat above the session rows as the menu scrolls.
          zIndex: 1,
          backgroundColor: 'background.paper',
          paddingY: 1.5,
          gap: 1,
          [`&:hover, &.${menuItemClasses.focusVisible}`]: {
            backgroundColor: 'background.paper',
            backgroundImage: (theme) =>
              `linear-gradient(${alpha(theme.palette.text.primary, 0.2)}, ${alpha(theme.palette.text.primary, 0.2)})`,
          },
        }}
      >
        <PlusIcon size={20} aria-hidden />
        New chat
      </MenuItem>
      {isPending ? (
        <MenuItem disabled>Loading chats…</MenuItem>
      ) : isError ? (
        <>
          <MenuItem disabled>Could not load recent chats</MenuItem>
          <MenuItem onClick={() => void refetch()}>Retry loading chats</MenuItem>
        </>
      ) : data.length === 0 ? (
        <MenuItem disabled>No recent chats</MenuItem>
      ) : (
        data.map((session) => (
          <MenuItem
            key={session.id}
            selected={sessionId === session.id}
            aria-current={sessionId === session.id ? 'true' : undefined}
            onClick={() => {
              selectSession({ sessionId: session.id });
              onClose();
            }}
            sx={{ gap: 2, minWidth: 0 }}
          >
            <Text color="inherit" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {getAgenticChatSessionTitle(session)}
            </Text>
            <Text component="span" variant="caption" color="inherit" noWrap>
              <AgenticChatSessionUpdatedTime when={session.updated} />
            </Text>
          </MenuItem>
        ))
      )}
    </Menu>
  );
}
